import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getRankInfo } from '../rank';

const API = process.env.REACT_APP_API_URL || '';
const GRADE_YEARS = ['8th Grade', '9th Grade', '10th Grade', '11th Grade', 'Senior (12th)'];

function resizeImage(file, maxSize = 300) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = reject;
    img.src = url;
  });
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M1 10s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z"/>
      <circle cx="10" cy="10" r="2.5"/>
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M13.875 13.875A8.966 8.966 0 0 1 10 15c-5.5 0-9-5-9-5a16.4 16.4 0 0 1 4.125-4.125M8.1 4.2A8.6 8.6 0 0 1 10 4c5.5 0 9 6 9 6a16.4 16.4 0 0 1-2.1 2.9M6.35 6.35A4 4 0 0 0 10 14a4 4 0 0 0 3.65-7.65"/>
      <line x1="2" y1="2" x2="18" y2="18"/>
    </svg>
  );
}

export default function Profile() {
  const { user, refreshUser, logout, clearSession } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const fileRef = useRef();
  const rank = getRankInfo(user?.rating ?? 0);

  const [tab, setTab] = useState('profile'); // 'profile' | 'account'

  // ── Profile tab state ──
  const [username, setUsername]   = useState(user?.display_name || user?.name || '');
  const [bio, setBio]             = useState(user?.bio || '');
  const [satScore, setSatScore]   = useState(user?.sat_score ?? '');
  const [gradeYear, setGradeYear] = useState(user?.grade_year || '');
  const [saving, setSaving]       = useState({});
  const [errors, setErrors]       = useState({});
  const [success, setSuccess]     = useState({});
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarSuccess, setAvatarSuccess] = useState(false);

  // Sync form fields whenever user data refreshes (e.g. after a successful save)
  useEffect(() => {
    if (!user) return;
    setUsername(user.display_name || user.name || '');
    setBio(user.bio || '');
    setSatScore(user.sat_score ?? '');
    setGradeYear(user.grade_year || '');
  }, [user]);

  // Username: client-side format validation
  function usernameFormatError(val) {
    const t = val.trim();
    if (t.length < 2) return 'At least 2 characters required.';
    if (t.length > 16) return 'Max 16 characters.';
    if (!/^[a-zA-Z0-9_.\-]+$/.test(t)) return 'Letters, numbers, _ . - only.';
    return null;
  }

  // 30-min username cooldown
  const lastChange = user?.last_name_change ? new Date(user.last_name_change) : null;
  const minsLeft = lastChange ? Math.ceil(30 - (Date.now() - lastChange.getTime()) / 60000) : 0;
  const onCooldown = minsLeft > 0;

  // ── Account tab state ──
  const [currentPw, setCurrentPw]   = useState('');
  const [newPw, setNewPw]           = useState('');
  const [confirmPw, setConfirmPw]   = useState('');
  const [showPw, setShowPw]         = useState(false);
  const [pwSaving, setPwSaving]     = useState(false);
  const [pwError, setPwError]       = useState('');
  const [pwSuccess, setPwSuccess]   = useState(false);

  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword]       = useState('');
  const [deleteLoading, setDeleteLoading]         = useState(false);
  const [deleteError, setDeleteError]             = useState('');
  const [showDeletePanel, setShowDeletePanel]     = useState(false);

  const winRate = user
    ? (user.wins + user.losses > 0 ? Math.round((user.wins / (user.wins + user.losses)) * 100) : 0)
    : 0;
  const hasPassword = user?.has_password;

  async function saveField(field, value) {
    setSaving(s => ({ ...s, [field]: true }));
    setErrors(e => ({ ...e, [field]: null }));
    setSuccess(s => ({ ...s, [field]: false }));
    try {
      await axios.put(`${API}/api/profile`, { [field]: value });
      await refreshUser();
      setSuccess(s => ({ ...s, [field]: true }));
      setTimeout(() => setSuccess(s => ({ ...s, [field]: false })), 2500);
    } catch (err) {
      setErrors(e => ({ ...e, [field]: err.response?.data?.error || 'Something went wrong.' }));
    } finally {
      setSaving(s => ({ ...s, [field]: false }));
    }
  }

  async function handleAvatarChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    // Reset input so same file can be re-selected after an error
    fileRef.current.value = '';
    // Client-side type check before processing
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setErrors(er => ({ ...er, avatar: 'Invalid file type. Use JPEG, PNG, GIF, or WebP.' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors(er => ({ ...er, avatar: 'File too large. Maximum 5 MB.' }));
      return;
    }
    setAvatarLoading(true);
    setAvatarSuccess(false);
    setErrors(er => ({ ...er, avatar: null }));
    try {
      const base64 = await resizeImage(file, 300);
      await axios.post(`${API}/api/profile/avatar`, { avatar: base64 });
      await refreshUser();
      setAvatarSuccess(true);
      setTimeout(() => setAvatarSuccess(false), 2500);
    } catch (err) {
      setErrors(er => ({ ...er, avatar: err.response?.data?.error || 'Upload failed.' }));
    } finally {
      setAvatarLoading(false);
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwError('');
    if (newPw.length < 8) { setPwError('New password must be at least 8 characters.'); return; }
    if (newPw !== confirmPw) { setPwError('Passwords do not match.'); return; }
    if (hasPassword && currentPw && currentPw === newPw) { setPwError('New password must be different from your current password.'); return; }
    setPwSaving(true);
    try {
      await axios.put(`${API}/api/account/password`, { currentPassword: currentPw, newPassword: newPw });
      setPwSuccess(true);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => setPwSuccess(false), 3000);
    } catch (err) {
      setPwError(err.response?.data?.error || 'Failed to change password.');
    } finally {
      setPwSaving(false);
    }
  }

  async function handleDeleteAccount(e) {
    e.preventDefault();
    setDeleteError('');
    if (deleteConfirmText !== 'DELETE') { setDeleteError('Type DELETE exactly to confirm.'); return; }
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/api/account`, {
        data: { password: deletePassword, confirmText: 'DELETE' },
      });
      // Server already deleted the Supabase auth user, so calling supabase.auth.signOut()
      // would 403. clearSession() wipes local state without that call.
      clearSession();
      navigate('/login', { replace: true });
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Failed to delete account.');
      setDeleteLoading(false);
    }
  }

  if (!user) return null;

  return (
    <div className="profile-page">
      <div className="profile-card">

        {/* Header */}
        <div className="profile-header">
          <div className="profile-avatar-wrap" onClick={() => fileRef.current?.click()} title="Click to change photo">
            {avatarLoading
              ? <div className="profile-avatar-spinner"><div className="queue-spinner" /></div>
              : user.avatar
                ? <img src={user.avatar} alt="" className="profile-avatar" />
                : <div className="profile-avatar profile-avatar-initials">
                    {(user.display_name || user.name || '?').charAt(0).toUpperCase()}
                  </div>
            }
            <div className="profile-avatar-overlay">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }} onChange={handleAvatarChange} />
          {errors.avatar && <div className="profile-field-error">{errors.avatar}</div>}
          {avatarSuccess && <div className="profile-success" style={{ fontSize: 12, marginTop: 4 }}>✓ Photo updated</div>}
          <div className="profile-header-info">
            <div className="profile-name-display">{user.display_name || user.name}</div>
            <div className="profile-rank-row">
              <img src={rank.icon} alt={rank.label} className="profile-rank-icon" />
              <span className="profile-rank-label" style={{ color: rank.color }}>{rank.label}</span>
            </div>
            <div className="profile-elo">{user.rating ?? 0} XP</div>
            {user.grade_year && <div className="profile-grade">{user.grade_year}</div>}
          </div>
        </div>

        {/* Stats */}
        <div className="profile-stats-row">
          <div className="profile-stat"><div className="profile-stat-val">{user.wins}</div><div className="profile-stat-lbl">Wins</div></div>
          <div className="profile-stat"><div className="profile-stat-val">{user.losses}</div><div className="profile-stat-lbl">Losses</div></div>
          <div className="profile-stat"><div className="profile-stat-val">{winRate}%</div><div className="profile-stat-lbl">Win Rate</div></div>
          <div className="profile-stat"><div className="profile-stat-val">{user.login_streak ?? 0}</div><div className="profile-stat-lbl">Streak</div></div>
          {user.sat_score && <div className="profile-stat"><div className="profile-stat-val">{user.sat_score}</div><div className="profile-stat-lbl">SAT Score</div></div>}
        </div>

        <div className="profile-divider" />

        {/* ── Tab layout: left sidebar + right content ─── */}
        <div className="profile-tab-layout">

          {/* Left sidebar */}
          <nav className="profile-sidebar">
            <button
              className={`profile-sidebar-btn${tab === 'profile' ? ' active' : ''}`}
              onClick={() => setTab('profile')}
            >
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden>
                <circle cx="10" cy="7" r="3"/>
                <path d="M3 17c0-3.3 3.1-6 7-6s7 2.7 7 6"/>
              </svg>
              General
            </button>
            <button
              className={`profile-sidebar-btn${tab === 'account' ? ' active' : ''}`}
              onClick={() => setTab('account')}
            >
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden>
                <circle cx="10" cy="10" r="3"/>
                <path d="M10 1v2M10 17v2M1 10h2M17 10h2M3.22 3.22l1.42 1.42M15.36 15.36l1.42 1.42M3.22 16.78l1.42-1.42M15.36 4.64l1.42-1.42"/>
              </svg>
              Account
            </button>
          </nav>

          {/* Right content pane */}
          <div className="profile-tab-content">

            {/* ── GENERAL TAB ──────────────────────────────── */}
            {tab === 'profile' && (
              <div className="profile-sections">

                <div className="profile-section">
                  <label className="profile-label">Username</label>
                  <div className="profile-input-row">
                    <input
                      className="profile-input" value={username} maxLength={16}
                      disabled={onCooldown}
                      onChange={e => { setUsername(e.target.value); setErrors(er => ({ ...er, display_name: null })); }}
                      placeholder="Enter a username"
                    />
                    <button className="btn btn-primary btn-sm" disabled={saving.display_name || onCooldown}
                      onClick={() => {
                        const fmtErr = usernameFormatError(username);
                        if (fmtErr) { setErrors(er => ({ ...er, display_name: fmtErr })); return; }
                        saveField('display_name', username);
                      }}>
                      {saving.display_name ? 'Saving…' : success.display_name ? '✓ Saved' : 'Save'}
                    </button>
                  </div>
                  {onCooldown && <div className="profile-hint" style={{ color: '#b45309' }}>Change available in {minsLeft} minute{minsLeft === 1 ? '' : 's'}.</div>}
                  {errors.display_name && <div className="profile-field-error">{errors.display_name}</div>}
                  {!onCooldown && !errors.display_name && <div className="profile-hint">2–16 characters. Updates everywhere.</div>}
                </div>

                <div className="profile-section">
                  <label className="profile-label">Grade Year</label>
                  <div className="profile-input-row">
                    <select className="profile-input" value={gradeYear} onChange={e => setGradeYear(e.target.value)}>
                      <option value="">Select grade…</option>
                      {GRADE_YEARS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <button className="btn btn-primary btn-sm" disabled={saving.grade_year}
                      onClick={() => saveField('grade_year', gradeYear)}>
                      {saving.grade_year ? 'Saving…' : success.grade_year ? '✓ Saved' : 'Save'}
                    </button>
                  </div>
                </div>

                <div className="profile-section">
                  <label className="profile-label">Real SAT Score</label>
                  <div className="profile-input-row">
                    <input className="profile-input" type="number" min={400} max={1600} step={10}
                      value={satScore} onChange={e => setSatScore(e.target.value)} placeholder="e.g. 1380" />
                    <button className="btn btn-primary btn-sm" disabled={saving.sat_score}
                      onClick={() => saveField('sat_score', satScore === '' ? null : Number(satScore))}>
                      {saving.sat_score ? 'Saving…' : success.sat_score ? '✓ Saved' : 'Save'}
                    </button>
                  </div>
                  {errors.sat_score && <div className="profile-field-error">{errors.sat_score}</div>}
                  <div className="profile-hint">400–1600. Only visible on your profile.</div>
                </div>

                <div className="profile-section">
                  <label className="profile-label">Bio</label>
                  <textarea className="profile-input profile-textarea" value={bio} maxLength={300} rows={4}
                    onChange={e => setBio(e.target.value)} placeholder="Tell other players about yourself…" />
                  <div className="profile-input-row" style={{ marginTop: 8 }}>
                    <span className="profile-hint">{bio.length}/300</span>
                    <button className="btn btn-primary btn-sm" disabled={saving.bio} onClick={() => saveField('bio', bio)}>
                      {saving.bio ? 'Saving…' : success.bio ? '✓ Saved' : 'Save Bio'}
                    </button>
                  </div>
                </div>

                <div className="profile-section">
                  <label className="profile-label">School</label>
                  <div className="profile-input-row">
                    <div className="profile-school-display">
                      {user.school ? <span className="school-flair">{user.school}</span> : <span className="profile-hint">No school set</span>}
                    </div>
                    <button className="btn btn-outline btn-sm" onClick={() => navigate('/school-select')}>
                      {user.school ? 'Change' : 'Set School'}
                    </button>
                  </div>
                </div>

                <div className="profile-section">
                  <label className="profile-label">Appearance</label>
                  <div className="profile-input-row" style={{ justifyContent: 'space-between' }}>
                    <span className="profile-hint" style={{ margin: 0 }}>Dark mode</span>
                    <button
                      type="button"
                      className={`toggle-switch${darkMode ? ' on' : ''}`}
                      onClick={toggleDarkMode}
                      aria-label="Toggle dark mode"
                    >
                      <span className="toggle-thumb" />
                    </button>
                  </div>
                </div>

              </div>
            )}

            {/* ── ACCOUNT TAB ──────────────────────────────── */}
            {tab === 'account' && (
              <div className="profile-sections">

                {/* ── Account Info ── */}
                <div className="profile-section">
                  <label className="profile-label">Account Info</label>
                  <div className="profile-info-row">
                    <span className="profile-info-label">Email</span>
                    <span className="profile-info-value">{user.email}</span>
                  </div>
                  <div className="profile-info-row">
                    <span className="profile-info-label">User ID</span>
                    <span className="profile-info-value profile-user-id">#{user.user_tag || user.id}</span>
                  </div>
                </div>

                <div className="profile-divider" />

                <div className="profile-section">
                  <label className="profile-label">{hasPassword ? 'Change Password' : 'Set Password'}</label>
                  <p className="profile-hint" style={{ marginBottom: 12 }}>
                    {hasPassword
                      ? 'Update your password. You\'ll need your current password to confirm.'
                      : 'Your account was created via Google. Add a password to also log in with email.'}
                  </p>
                  <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {hasPassword && (
                      <div className="pw-input-wrap">
                        <input type={showPw ? 'text' : 'password'} className="profile-input"
                          placeholder="Current password" value={currentPw}
                          onChange={e => setCurrentPw(e.target.value)} required />
                        <button type="button" className="pw-toggle" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
                          {showPw ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                      </div>
                    )}
                    <div className="pw-input-wrap">
                      <input type={showPw ? 'text' : 'password'} className="profile-input"
                        placeholder="New password (min 8 chars)" value={newPw}
                        onChange={e => setNewPw(e.target.value)} required />
                      {!hasPassword && (
                        <button type="button" className="pw-toggle" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
                          {showPw ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                      )}
                    </div>
                    <input type={showPw ? 'text' : 'password'} className="profile-input"
                      placeholder="Confirm new password" value={confirmPw}
                      onChange={e => setConfirmPw(e.target.value)} required />
                    {pwError && <div className="profile-field-error">{pwError}</div>}
                    {pwSuccess && <div className="profile-success">✓ Password updated successfully.</div>}
                    <button type="submit" className="btn btn-primary btn-sm" disabled={pwSaving} style={{ alignSelf: 'flex-end' }}>
                      {pwSaving ? 'Saving…' : hasPassword ? 'Update Password' : 'Set Password'}
                    </button>
                  </form>
                </div>

                <div className="profile-divider" />

                <div className="profile-section">
                  <label className="profile-label" style={{ color: '#b91c1c' }}>Delete Account</label>
                  <p className="profile-hint" style={{ marginBottom: 12 }}>
                    Permanently deletes your account, stats, and match history. This cannot be undone.
                  </p>
                  {!showDeletePanel ? (
                    <button className="btn btn-danger btn-sm" onClick={() => setShowDeletePanel(true)}>
                      Delete My Account
                    </button>
                  ) : (
                    <form onSubmit={handleDeleteAccount} className="delete-confirm-panel">
                      {hasPassword && (
                        <input type="password" className="profile-input" placeholder="Enter your password to confirm"
                          value={deletePassword} onChange={e => setDeletePassword(e.target.value)} required />
                      )}
                      <input className="profile-input" placeholder='Type DELETE to confirm'
                        value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} required />
                      {deleteError && <div className="profile-field-error">{deleteError}</div>}
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowDeletePanel(false); setDeleteError(''); setDeleteConfirmText(''); setDeletePassword(''); }}>
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-danger btn-sm" disabled={deleteLoading}>
                          {deleteLoading ? 'Deleting…' : 'Permanently Delete'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>

              </div>
            )}

          </div>{/* end profile-tab-content */}
        </div>{/* end profile-tab-layout */}

      </div>
    </div>
  );
}
